import {Types} from "../types";

import {createArray} from "./collections";
import * as JsDate from "./js-date";
import {chain} from "./fs";

type DateObject = Types.Common.Utils.DateObject.DateObject;

export const LOCAL_TIMEZONE_OFFSET_MINUTES = -(new Date().getTimezoneOffset());

export const getWeekDays = (date:DateObject) => {
    const start = toDate(getWeekStart(date)).getTime();

    return createArray(7).map((i) => {
        const wd = new Date(start);
        wd.setDate(wd.getDate() + i);
        return Object.assign({}, parseDate(wd), {dow: i,});
    });
};

export const getMonthStart = (date:DateObject) => {
    return {...date, day: 1};
};

export const getWeekStart = (date:DateObject) => {
    const d = toDate(date);

    const start = new Date(d.getTime());
    start.setDate(start.getDate() - d.getDay());
    return parseDate(start);
};

export const getMonthLength = (date:DateObject) => {
    return chain(
        toDate({...date, day: 1}),
        (d) => JsDate.addMonth(d, 1),
        (d) => JsDate.addDate(d, -1),
        (d) => d.getDate(),
    );
};

export const getMonthWeeks = (date:DateObject) => {
    const monthStart = getMonthStart(date);

    let weeks = [];
    let index = getWeekStart(monthStart);
    for (;;) {
        const weekDays = getWeekDays(index);
        if (weekDays.find((wd) => wd.month === date.month)) {
           weeks.push(weekDays);
           index = addDate(index, 7);
        } else {
            break;
        }
    }
    return weeks;
};

export const getMonthDays = (date:DateObject) => {
    return chain(
        getMonthStart(date),
        (start) => createArray(getMonthLength(date)).map((i) => addDate(start, i))
    );
};

export const toDate = (date:DateObject) => new Date(date.year, date.month - 1, date.day || 1, 0);

export const parseDate = (val:string | number | Date):DateObject => {
    if (val == null || val === "") {
        return null;
    }

    let jsDate:Date;
    if (val instanceof Date) {
        jsDate = val;
    } else if (typeof val === "number") {
        jsDate = new Date(val);
    } else if (typeof val === "string") {
        jsDate = new Date(val);
        jsDate.setMinutes(jsDate.getMinutes() + jsDate.getTimezoneOffset());
    }

    if (isNaN(jsDate.getTime())) {
        return null;
    }

    return ({
        year: jsDate.getFullYear(),
        month: jsDate.getMonth() + 1,
        day: jsDate.getDate(),
    });
};

export const sameDate = (d1:any, d2:DateObject) => {
    if (d1 == null && d2 == null) {
        return true;
    } else if (d1 == null || d2 == null) {
        return false;
    }

    return d1.day === d2.day &&
        d1.month === d2.month &&
        d1.year === d2.year;
};

const importances = ["year", "month", "day"];
export const lt = (d1:DateObject, d2:DateObject) => {
    for (const a of importances) {
        if (d1[a] !== d2[a]) {
            return d1[a] < d2[a];
        }
    }
    return false; // Don't accept equals
};

export const le = (d1:DateObject, d2:DateObject) => {
    for (const a of importances) {
        if (d1[a] !== d2[a]) {
            return d1[a] < d2[a];
        }
    }
    return true; // Accept equals
};

export const gt = (d1:DateObject, d2:DateObject) => {
    for (const a of importances) {
        if (d1[a] !== d2[a]) {
            return d1[a] > d2[a];
        }
    }
    return false; // Don't accept equals
};

export const ge = (d1:DateObject, d2:DateObject) => {
    for (const a of importances) {
        if (d1[a] !== d2[a]) {
            return d1[a] > d2[a];
        }
    }
    return true; // Accept equals
};

export const addDate = (date:DateObject, delta:number) => parseDate(JsDate.addDate(toDate(date), delta));

export const addMonth = (date:DateObject, delta:number) => parseDate(JsDate.addMonth(toDate(date), delta));

export const addYear = (date:DateObject, delta:number) => parseDate(JsDate.addYear(toDate(date), delta));

export const getMonthRange = ({year, month}:DateObject) => {
    const monthStart = {year, month, day: 1};
    return ({from: monthStart, to: addMonth(monthStart, 1)});
};

export const getDow = (date:DateObject) => {
    if (date.dow) {
        return date.dow;
    }
    return toDate(date).getDay();
};

export const toString = (date:DateObject):String => {
    return date['year'] + '-' + date['month'] + '-' + date['day']
}


export function dateDiff(d1:DateObject, d2:DateObject) {
    return (toDate(d1).getTime() - toDate(d2).getTime()) / (24*60*60*1000)
}

export const today = () => {
    return parseDate(new Date());
};

export const tomorrow = () => parseDate(JsDate.addDate(new Date(), 1));

export const yesterday = () => addDate(today(), -1);

export const singleDateRange = (date:DateObject) => ({from: date, to: addDate(date, 1)});

export const toJsDate = (d:DateObject, timezoneOffsetMinutes:number = LOCAL_TIMEZONE_OFFSET_MINUTES) => {
    return new Date(d.year, d.month - 1, d.day, 0, LOCAL_TIMEZONE_OFFSET_MINUTES - timezoneOffsetMinutes);
};

/**
 * timeString: 09:00
 */
export const toJsDate_v2 = (d, timeString) => {
    const timeSplit = timeString.split(":");
    return new Date(d.year, d.month - 1, d.day, +timeSplit[0], +timeSplit[1]);
};

export const min = (d1:DateObject,d2:DateObject) => lt(d1, d2) ? d1 : d2;

export const max = (d1:DateObject,d2:DateObject) => gt(d1, d2) ? d1 : d2;

